import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  QuoteApprovalDecideRequest,
  QuoteApprovalPendingItem,
  QuoteApprovalPublicResponse,
  QuoteApprovalRequest,
  QuoteApprovalResponse
} from '../models/quote-approval.model';
import { CrmUserResponse } from '../../users/models/user.model';

/**
 * API del flujo de aprobación interna de cotizaciones (RF-001/RF-002).
 * Endpoints autenticados (JWT + businessId) y públicos por token (página /#/aprobacion/:token).
 */
@Injectable({
  providedIn: 'root'
})
export class QuoteApprovalApiService {
  private readonly apiUrl = `${environment.apiUrl}api`;

  constructor(private http: HttpClient) { }

  // POST /api/quotes/{id}/request-approval — solicita aprobación interna (cotización en Borrador).
  // Efectos: cotización → "Pendiente de Aprobación"; correo al aprobador con link tokenizado.
  // Errores: 409 ya hay PENDING; 422 no está en Borrador / aprobador inválido / aprobador = solicitante.
  requestApproval(quoteId: number, businessId: number, request: QuoteApprovalRequest): Observable<QuoteApprovalResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.post<QuoteApprovalResponse>(`${this.apiUrl}/quotes/${quoteId}/request-approval`, request, { params });
  }

  // POST /api/quote-approvals/{approvalId}/decide — decide desde el CRM (solo el aprobador asignado;
  // Super Admin override). APPROVED → quote "Aprobada"; REJECTED/CHANGES_REQUESTED → vuelve a "Borrador".
  // Errores: 400 decision inválida; 403; 404; 409 ya decidida.
  decide(approvalId: number, businessId: number, request: QuoteApprovalDecideRequest): Observable<QuoteApprovalResponse> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.post<QuoteApprovalResponse>(`${this.apiUrl}/quote-approvals/${approvalId}/decide`, request, { params });
  }

  // GET /api/quotes/{id}/approvals — historial completo de aprobaciones (más reciente primero).
  getApprovals(quoteId: number, businessId: number): Observable<QuoteApprovalResponse[]> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<QuoteApprovalResponse[]>(`${this.apiUrl}/quotes/${quoteId}/approvals`, { params });
  }

  // GET /api/quote-approvals/pending — bandeja de aprobaciones pendientes del usuario del token.
  getPendingApprovals(businessId: number): Observable<QuoteApprovalPendingItem[]> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<QuoteApprovalPendingItem[]>(`${this.apiUrl}/quote-approvals/pending`, { params })
      .pipe(map(r => r ?? []));
  }

  // GET /api/auth/users/selectable?businessId — usuarios ACTIVOS del business (selector de aprobador).
  // En la UI se excluye al usuario actual (el aprobador debe ser distinto del solicitante).
  //
  // Se usa /selectable y no /api/auth/users porque este último exige `users:VIEW`
  // (módulo Usuarios de Configuración) y desde V191 los roles comerciales ya no lo
  // tienen: el picker se quedaría vacío. /selectable es el endpoint de pickers,
  // abierto a cualquier miembro del business y ya filtrado por isActive=true.
  getActiveUsers(businessId: number): Observable<CrmUserResponse[]> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<CrmUserResponse[]>(`${this.apiUrl}/auth/users/selectable`, { params })
      .pipe(map(r => r ?? []));
  }

  // ---- Públicos por token (sin JWT — página /#/aprobacion/{token}) ----

  // GET /api/quote-approvals/public/{token} — resumen para decidir. 404 token inválido.
  getPublicApproval(token: string): Observable<QuoteApprovalPublicResponse> {
    return this.http.get<QuoteApprovalPublicResponse>(`${this.apiUrl}/quote-approvals/public/${token}`);
  }

  // POST /api/quote-approvals/public/{token}/decide — token de un solo uso (segundo intento → 409).
  decidePublic(token: string, request: QuoteApprovalDecideRequest): Observable<QuoteApprovalPublicResponse> {
    return this.http.post<QuoteApprovalPublicResponse>(`${this.apiUrl}/quote-approvals/public/${token}/decide`, request);
  }
}
